<script setup lang="ts">
import type { Group } from '~/models/Group'
import { GroupType } from '~/models/Group'
import type { FiltersQueryParams } from '~/models/Filter'
import { useRouter } from 'vue-router'
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { KsDropdown } from '@aschehoug/kloss'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '~/stores/auth'
import useGroupsStore from '~/stores/groups'
import useSubjectsStore from '~/stores/subjects'
import useDialogStore from '~/stores/dialog'
import usePlannerStore from '~/stores/planner'
import arrayUtils from '~/utils/arrayUtils'
import useFilterStore from '~/stores/filter'
import useTime from '~/composables/useTime'
import useQueryParams from '~/composables/queryParams'
import { QueryParamKey } from '~/models/QueryParamKeys'

const { t } = useI18n()
const { truthy } = arrayUtils()
const { showDialog } = useDialogStore()

const authStore = useAuthStore()
const { setUserActiveGroup } = authStore
const { activeUserGroups, isLoading } = storeToRefs(authStore)

const { selectedWeekNumber } = storeToRefs(usePlannerStore())
const { currentWeekNumber } = useTime()

const groupsStore = useGroupsStore()
const { updateGroup, loadGroups, loadGroupMembers, hasGroupMembers } = groupsStore
const { selectedGroup, sortedGroups } = storeToRefs(groupsStore)

const { setGrepCodes, setCurrentSubject } = useSubjectsStore()
const { setSelectedGrade } = useFilterStore()

const router = useRouter()
const routeName = computed(() => router.currentRoute.value.name)

/**
 * When to set subject in store on group change
 */
const shouldUpdateSubject = computed(() => ['home', 'subject'].includes(<string>routeName.value))

/**
 * Open dialog if groups is missing grade or subject...
 * ... or if it's a basis group (i.e a class) since it may span multiple subjects
 */
const openDialog = (group: Nullable<Group>) => group && !(
  group.grade
  && group.subjects.length
  && group.groupType !== GroupType.Basis
)

const isSelectingGroup = ref(false)

/**
 * Update users active filters, and create a new year plan
 */
async function selectGroup() {
  isSelectingGroup.value = true

  // Reset user filters if group is deselected
  if (!selectedGroup.value) {
    if (routeName.value !== 'subject') setCurrentSubject('')
    await setUserActiveGroup(undefined)
    isSelectingGroup.value = false
    return
  }

  const grepCodes = [selectedGroup.value.grepCode].filter(truthy)
  setGrepCodes(grepCodes)

  if (openDialog(selectedGroup.value)) {
    selectedGroup.value = await showDialog<Group>(
      defineAsyncComponent(() => import('~/components/group/ActiveGroupDialog.vue')),
      { group: selectedGroup.value },
    )
    // Whether you're able to save the group is not really important
    // ... it's mostly just a bonus
    await updateGroup(selectedGroup.value)
      .catch((error) => console.log(error))
  }

  if (selectedGroup.value.grade) {
    setSelectedGrade(selectedGroup.value.grade)
  }
  if (selectedGroup.value.subjects.length && shouldUpdateSubject.value) {
    setCurrentSubject(selectedGroup.value.subjects[0])
  }

  selectedWeekNumber.value = selectedWeekNumber.value > 0 ? selectedWeekNumber.value : currentWeekNumber
  await setUserActiveGroup(selectedGroup.value)
  await forwardToSubject()

  isSelectingGroup.value = false
}

/**
 * Load grep codes and group members on each group change
 */
watch(selectedGroup, (group, oldValue) => {
    if (!group && !!oldValue && !isSelectingGroup.value) {
      selectGroup()
    }
    if (group && !hasGroupMembers(group)) {
      loadGroupMembers(group)
    }
}, { immediate: true })

/**
 * Forward browser to relevant subject view
 */
async function forwardToSubject() {
  if (!selectedGroup.value || !selectedGroup.value.subjects.length) return
  const forwardSubject = selectedGroup.value.subjects[0]
  const isValidRoute = ['subject'].includes(<string>routeName.value)
  if (forwardSubject && isValidRoute) {
    await router.push({ name: 'subject', params: { subject: forwardSubject.toLowerCase() } })
  }
}

const { get } = useQueryParams<FiltersQueryParams>(QueryParamKey.Filters)

/**
 * Loads groups, then set grade and subject
 */
onMounted(async() => {
  await loadGroups()
  selectedGroup.value = activeUserGroups.value[0] ?? null
  if (selectedGroup.value?.grepCode) {
    setGrepCodes([ selectedGroup.value.grepCode ])
  }

  // If a grade is set in the filter URL, we should not change it just because the group has a grade
  const hasGrade = get()[QueryParamKey.Grade]
  if (selectedGroup.value?.grade && !hasGrade) {
    setSelectedGrade(selectedGroup.value.grade)
  }

  selectedWeekNumber.value = selectedWeekNumber.value > 0 ? selectedWeekNumber.value : currentWeekNumber
})
</script>
<template>
  <KsDropdown
    v-model="selectedGroup"
    :options="sortedGroups"
    :option-label="opt => opt.name"
    :placeholder="t('header.chooseGroup')"
    shape="rounded"
    :selectable="opt => !isLoading"
    :resettable="true"
    autolabel
    @select="selectGroup"
    @reset="selectGroup"
  />
</template>
